import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import Sheet from '../../shared/ui/Sheet';
import { PrimaryButton } from '../../shared/ui';
import { updateUserProfile } from '../../shared/services/userService';

// Mirrors iOS EditDriverProfileView "Personal Information" section —
// first/last name and email are read-only there; only phone is editable.
// Writes only users/{uid}.phone (same as FirebaseManager.updateUserProfile).
const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
      {label}
    </span>
    {children}
  </label>
);

const inputCls =
  'w-full px-3 py-2.5 rounded-xl bg-gray-100 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#FF6B35]/40';

const readOnlyCls =
  'w-full px-3 py-2.5 rounded-xl bg-gray-50 text-sm text-gray-500 outline-none';

export default function EditProfileSheet({ open, onClose, uid, profile, onSaved }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setFirstName(profile?.firstName || '');
      setLastName(profile?.lastName || '');
      setEmail(profile?.email || '');
      setPhone(profile?.phone || profile?.phoneNumber || '');
      setError('');
      setSaving(false);
    }
  }, [open, profile]);

  async function save() {
    if (!uid || saving) return;
    setSaving(true);
    setError('');
    try {
      // iOS parity — name/email are read-only; only phone is editable.
      const fields = { phone: phone.trim() };
      await updateUserProfile(uid, fields);
      onSaved?.(fields);
      onClose();
    } catch (e) {
      console.error('updateUserProfile', e);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="Edit Profile">
      <div className="px-4 pb-6 space-y-4">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
          Personal Information
        </p>
        <Field label="First Name">
          <input className={readOnlyCls} value={firstName} readOnly tabIndex={-1} />
        </Field>
        <Field label="Last Name">
          <input className={readOnlyCls} value={lastName} readOnly tabIndex={-1} />
        </Field>
        <Field label="Email">
          <input className={readOnlyCls} type="email" value={email} readOnly tabIndex={-1} />
        </Field>
        <Field label="Phone">
          <input
            className={inputCls}
            type="tel"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="Phone"
            autoComplete="tel"
          />
        </Field>
        {error && <p className="text-sm text-red-500">{error}</p>}
        <PrimaryButton onClick={save} disabled={saving}>
          {saving ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Saving…
            </span>
          ) : (
            'Save'
          )}
        </PrimaryButton>
      </div>
    </Sheet>
  );
}
